import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsBox } from '../common/basic/QcsBox';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { HtmlViewer } from '../common/html/HtmlViewer';
import { SignUser } from '../common/SignUser';
import { TourniquetButtonContent } from '../tourniquet/TourniquetButtonContent';
import { TourniquetCaption } from '../tourniquet/TourniquetCaption';
import { TourniquetSlideCard } from '../tourniquet/TourniquetStyles';
import QcsAlert from '../common/basic/QcsAlert';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { TrainingRunQuestion } from './TrainingRunQuestionnaire';
import { trainingVersionService } from '@api/services/services';
import { resources } from '@api/resources';
import { AnswerResponseType } from '@api/models/questionnaire/AnswerResponseType';

interface Props {
  versionId: string;
  lang: string;
  questions: TrainingRunQuestion[];
  successRate?: number;
  persons: ReferenceDto[];
  afterTrainingContent?: string;
  hasSlides: boolean;
  isPlayground?: boolean;
  onGoToTraining: () => void;
  onGoToQuestionnaire: () => void;
  onComplete: () => void;
  onAbort: () => void;
}

export const TrainingRunOverview: FC<Props> = ({
  versionId,
  lang,
  questions,
  successRate: requiredSuccessRate,
  persons,
  afterTrainingContent,
  hasSlides,
  isPlayground,
  onGoToTraining,
  onGoToQuestionnaire,
  onComplete,
  onAbort,
}) => {
  const { t } = useTranslation();
  const [remainingPersons, setRemainingPersons] = useState<ReferenceDto[]>(persons);

  const correctCount = questions.filter((x) => x.answerResponse === AnswerResponseType.Correct).length;
  const totalCount = questions.length;
  const successRate = Math.floor((correctCount / totalCount) * 100);
  const isOk = successRate >= (requiredSuccessRate ?? 0);

  const handleSignSave = async (userId: string, documentId: string) => {
    if (isPlayground) return;
    await trainingVersionService.signTraining(versionId, {
      person: userId,
      signatureDocumentId: documentId,
      trainingLanguageVariant: lang,
      questionnaireLanguageVariant: lang,
      answers: questions.map((q) => ({ questionId: q.id!, answerId: q.selectedAnswerId! })),
    });
    setRemainingPersons((prev) => prev.filter((x) => x.id !== userId));
  };

  return (
    <TourniquetButtonContent onAbort={isOk && remainingPersons.length === 0 ? undefined : onAbort} mobileGap={true}>
      <TourniquetSlideCard>
        <QcsBox>
          <TourniquetCaption title={t('tourniquet.trainingSignTitle')} />
          <QcsTypography sx={{ mb: 1 }} align="center">
            {t('tourniquet.yourScore')}
            {': '}
            {successRate}%
          </QcsTypography>
          {!!requiredSuccessRate && (
            <QcsTypography variant="caption" sx={{ mb: 1, display: 'block' }} align="center">
              {t('tourniquet.minimalRequiredScore')}
              {': '}
              {requiredSuccessRate}%
            </QcsTypography>
          )}
          {isOk ? (
            <>
              <QcsAlert severity="success" sx={{ margin: '0 auto', marginBottom: '1rem' }}>
                {t('tourniquet.successQuestionnaire')}
              </QcsAlert>
              {!!afterTrainingContent && (
                <QcsBox sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <HtmlViewer html={afterTrainingContent} />
                </QcsBox>
              )}
              {remainingPersons.length !== 0 && (
                <SignUser userList={remainingPersons} save={handleSignSave} errorResource={[resources.signTraining]} />
              )}
              {remainingPersons.length === 0 && (
                <QcsLoadingButton
                  variant="contained"
                  sx={{ margin: '0 auto', display: 'block' }}
                  onClick={onComplete}
                  resourceLoading={resources.signTraining}
                >
                  {' '}
                  {t('common.close')}
                </QcsLoadingButton>
              )}
            </>
          ) : (
            <>
              <QcsAlert severity="error" sx={{ margin: '0 auto' }}>
                {t('tourniquet.wrongQuestionaire')}
              </QcsAlert>
              <QcsBox
                sx={{
                  width: 'max-content',
                  margin: '0 auto',
                  marginTop: '1rem',
                  display: 'flex',
                  gap: '1rem',
                }}
              >
                {hasSlides && (
                  <QcsButton variant="outlined" onClick={onGoToTraining}>
                    {t('tourniquet.retryTraining')}
                  </QcsButton>
                )}
                <QcsButton variant="outlined" onClick={onGoToQuestionnaire}>
                  {t('tourniquet.retryQuestionnaire')}
                </QcsButton>
              </QcsBox>
            </>
          )}
        </QcsBox>
      </TourniquetSlideCard>
    </TourniquetButtonContent>
  );
};
